import { isAfter, isBefore, isSameDay, startOfMonth, addDays, subDays, endOfMonth, isSameWeek } from 'date-fns'
import React, { ReactElement, useEffect, useState } from 'react'
import { StyleSheet, View } from 'react-native'

import { dateFormatter, parseDate } from '../../helper'
import { Color, Spacing } from '../../style'
import { FormField } from '../form'
import { Menu, MenuItemData } from '../menu'
import { TextInput } from '../textinput'
import { Text } from '../typography'
import { Button, ButtonProps } from '../button'

import Calendar, { Value as CalendarValue } from './calendar'

import { CalendarProps } from '.'

export interface PresetValue {
  title: string
  value: CalendarValue
}

export interface PresetConfig {
  items: PresetValue[]
  otherItemTitle?: string
  extra?: ReactElement
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
  },
  left: {
    borderRightWidth: 1,
    borderColor: Color.calmGrey,
    paddingVertical: Spacing.S,
  },
  right: {},
  rightContent: {
    flex: 1,
    paddingHorizontal: Spacing.M,
  },
  form: {
    paddingTop: Spacing.S,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  field: {
    minHeight: 60,
  },
  inline: {
    paddingHorizontal: Spacing.XS,
  },
  input: {
    minHeight: 31,
    maxWidth: 90,
  },
  dash: {
    alignSelf: 'flex-start',
    lineHeight: 31,
  },
  menu: {
    minWidth: 200,
  },
  preset: {
    fontSize: 12,
  },
  buttons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    borderTopWidth: 1,
    borderColor: Color.calmGrey,
    paddingVertical: Spacing.M,
    paddingHorizontal: Spacing.S,
  },
  button: {
    marginHorizontal: Spacing.XS,
  },
  custom: {
    minHeight: 40,
    flexDirection: 'row',
    alignItems: 'center',
    borderRightWidth: 3,
    borderRightColor: Color.transparent,
  },
  customActive: {
    backgroundColor: Color.lightGreyBlue,
    borderRightColor: Color.greyBlue,
  },
  customText: {
    flex: 1,
    marginHorizontal: Spacing.S,
  },
  customActiveText: {
    color: Color.darkGreyBlue,
  },
  calendar: {
    flex: 1,
  },
})

export interface Props extends CalendarProps {
  invalidDateText?: string
  preset?: PresetConfig
  buttons?: ButtonProps[]
}
const DatePicker = (props: Props) => {
  const {
    language = 'en',
    invalidDateText,
    preset,
    buttons,
    value: pickerValue,
    date: pickerDate,
    onValueChange,
    onDateChange,
    ...otherProps
  } = props
  const [value, setValue] = useState(pickerValue || preset?.items?.[0].value)
  const [startAt, endAt] = value || []
  const [shownMonthStart, setShownMonthStart] = useState(startOfMonth(pickerDate || new Date()))
  const getInput = () => {
    const start = startAt ? dateFormatter(startAt, 'PP', language) : ''
    const end = endAt ? dateFormatter(endAt, 'PP', language) : start || ''
    return { start, end }
  }

  const [input, setInput] = useState(getInput())
  const [inputError, setInputError] = useState<{ start?: string; end?: string }>({})

  const isOutsideCalendar = (date: Date) => {
    const shownMonthEnd = endOfMonth(shownMonthStart)
    if (isAfter(date, shownMonthStart)) {
      if (isBefore(date, shownMonthEnd)) {
        return false
      } else {
        return !isSameWeek(date, shownMonthEnd, {
          weekStartsOn: 1,
        })
      }
    } else {
      return !isSameWeek(date, shownMonthStart, {
        weekStartsOn: 1,
      })
    }
  }

  useEffect(() => {
    setValue(pickerValue || preset?.items?.[0].value)
  }, [pickerValue])

  useEffect(() => {
    updateDate(pickerDate || new Date())
  }, [pickerDate])

  const presetIsSelected = (preset: PresetValue) => {
    if (!value?.length) return false
    const fullValue = [value[0], value[1] || value[0]]
    const fullPreset = [preset.value[0], preset.value[1] || preset.value[0]]
    return isSameDay(fullPreset[0], fullValue[0]) && isSameDay(fullPreset[1], fullValue[1])
  }
  const selectedPresetIndex = preset?.items.findIndex(presetIsSelected)
  const presetData = preset?.items?.map((item, index) => ({
    ...item,
    key: item.title,
    selected: index === selectedPresetIndex,
    onPress: () => {
      updateValue(item.value)
    },
  })) as MenuItemData[]

  useEffect(() => {
    setInput(getInput())
    setInputError({})

    if (startAt && isOutsideCalendar(startAt)) {
      if (endAt) {
        if (isOutsideCalendar(endAt)) {
          if (isBefore(shownMonthStart, startOfMonth(startAt))) {
            updateDate(startAt)
          } else {
            updateDate(endAt)
          }
        }
      } else {
        updateDate(startAt)
      }
    }
  }, [startAt, endAt])

  const updateInput = (key: 'start' | 'end') => (text: string) => {
    setInput({ ...input, [key]: text })
  }

  const blurInput = (key: 'start' | 'end') => () => {
    const text = input[key]
    let date
    try {
      date = text ? parseDate(text, language) : undefined
    } catch (_) {}
    const isValid =
      !isNaN(date?.getTime() || NaN) &&
      (!props.disabled?.after || (date && isBefore(date, addDays(props.disabled.after, 1)))) &&
      (!props.disabled?.before || (date && isAfter(date, subDays(props.disabled.before, 1))))

    if (isValid) {
      let newValue
      if (key === 'start') {
        newValue = [date, ...(endAt ? [endAt] : [])] as CalendarValue
      } else {
        newValue = [startAt || date, date] as CalendarValue
      }
      updateValue(newValue.sort((a, b) => a.getTime() - b.getTime()))
    } else {
      setInputError({ ...inputError, [key]: invalidDateText || '' })
    }
  }

  const updateValue = (newValue: CalendarValue) => {
    setValue(newValue)
    onValueChange?.(newValue)
  }

  const updateDate = (date: Date) => {
    if (isOutsideCalendar(date)) {
      setShownMonthStart(startOfMonth(date))
      onDateChange?.(startOfMonth(date))
    }
  }

  const renderPreset = () => {
    const customStyle = [styles.custom, selectedPresetIndex === -1 && styles.customActive]
    const textStyles = [styles.preset, styles.customText, selectedPresetIndex === -1 && styles.customActiveText]

    return (
      <>
        <Menu data={presetData} textStyle={styles.preset} style={styles.menu} />
        {preset?.otherItemTitle && selectedPresetIndex === -1 && (
          <View style={customStyle}>
            <Text style={textStyles} level={6}>
              {preset?.otherItemTitle}
            </Text>
          </View>
        )}
      </>
    )
  }
  return (
    <View style={styles.container}>
      <View style={styles.left}>
        {presetData && renderPreset()}
        {preset?.extra}
      </View>
      <View style={styles.right}>
        <View style={styles.rightContent}>
          <Calendar
            value={value}
            onValueChange={updateValue}
            date={shownMonthStart}
            onDateChange={updateDate}
            style={styles.calendar}
            {...otherProps}
          />
          <View style={styles.form}>
            <FormField error={inputError.start} style={styles.field}>
              <TextInput
                inlineStyle={styles.inline}
                inputStyle={styles.input}
                value={input.start}
                onChangeText={updateInput('start')}
                onBlur={blurInput('start')}
                error={inputError.start !== undefined}
                testID="DatePicker.Input.Start"
              />
            </FormField>
            <Text style={styles.dash}> - </Text>
            <FormField error={inputError.end} style={styles.field}>
              <TextInput
                inlineStyle={styles.inline}
                inputStyle={styles.input}
                value={input.end}
                onChangeText={updateInput('end')}
                onBlur={blurInput('end')}
                error={inputError.end !== undefined}
                testID="DatePicker.Input.End"
              />
            </FormField>
          </View>
        </View>
        {buttons && (
          <View style={styles.buttons}>
            {buttons.map((buttonProps, index) => {
              const { style: buttonStyle, ...otherButtonProps } = buttonProps
              return (
                <Button key={`button-${index}`} size="S" style={[styles.button, buttonStyle]} {...otherButtonProps} />
              )
            })}
          </View>
        )}
      </View>
    </View>
  )
}

export default DatePicker
